import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Printer } from 'lucide-react';

const BillingDialog = ({
  open,
  onOpenChange,
  billingTable,
  billingOrders,
  onGenerateBill
}) => {
  // Calculate totals for billing
  const calculateBillingTotals = () => {
    const subtotal = billingOrders.reduce((sum, o) => sum + (o.subtotal || o.total_amount), 0);
    const discountTotal = billingOrders.reduce((sum, o) => sum + (o.discount || 0), 0);
    const taxTotal = billingOrders.reduce((sum, o) => sum + (o.tax || 0), 0);
    const total = billingOrders.reduce((sum, o) => sum + o.total_amount, 0);
    return { subtotal, discount: discountTotal, tax: taxTotal, total };
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Generate Bill - Table {billingTable?.table_number}</DialogTitle>
        </DialogHeader>
        {billingTable && billingOrders.length > 0 && (
          <div className="space-y-4">
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {billingOrders.map((order, idx) => (
                <div key={order.id} className="p-3 border rounded-lg bg-muted/50">
                  <div className="flex justify-between items-center mb-2">
                    <Badge variant="outline">Order #{idx + 1}</Badge>
                    <span className="text-xs text-muted-foreground">
                      {new Date(order.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="text-sm space-y-1">
                    {order.items?.map((item, i) => (
                      <div key={i} className="flex justify-between text-muted-foreground">
                        <span>{item.item_name} × {item.quantity}</span>
                        <span>₹{item.price * item.quantity}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between font-medium mt-2 pt-2 border-t text-sm">
                    <span>Order Total</span>
                    <span>₹{order.total_amount}</span>
                  </div>
                </div>
              ))}
            </div>

            <Card className="bg-primary/5">
              <CardContent className="p-4 space-y-2">
                <div className="flex justify-between">
                  <span>Total Orders</span>
                  <span>{billingOrders.length}</span>
                </div>
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>₹{calculateBillingTotals().subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-red-600">
                  <span>Discount</span>
                  <span>-₹{calculateBillingTotals().discount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Tax</span>
                  <span>₹{calculateBillingTotals().tax.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-xl border-t pt-2">
                  <span>Grand Total</span>
                  <span>₹{calculateBillingTotals().total.toFixed(2)}</span>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-2">
              <Label>Payment Method</Label>
              <div className="grid grid-cols-3 gap-2">
                <Button variant="outline" className="h-14 flex-col" onClick={() => onGenerateBill('cash')} data-testid="bill-pay-cash">
                  <span className="text-xl">💵</span>
                  <span className="text-xs">Cash</span>
                </Button>
                <Button variant="outline" className="h-14 flex-col" onClick={() => onGenerateBill('card')} data-testid="bill-pay-card">
                  <span className="text-xl">💳</span>
                  <span className="text-xs">Card</span>
                </Button>
                <Button variant="outline" className="h-14 flex-col" onClick={() => onGenerateBill('upi')} data-testid="bill-pay-upi">
                  <span className="text-xl">📱</span>
                  <span className="text-xs">UPI</span>
                </Button>
              </div>
            </div>

            <Button variant="outline" className="w-full" data-testid="print-bill-btn">
              <Printer className="h-4 w-4 mr-2" /> Print Bill
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BillingDialog;
